/* EcologySheet — the in-battle 🌿 Ecology button opens this sheet.
 *
 * It's an optional deep-dive so players can learn about the two
 * species currently in the arena WITHOUT pausing gameplay to leave
 * the screen. The sheet slides in from the bottom, covers ~60% of
 * the viewport and dismisses on tap-outside / close-button / Esc.
 *
 * Data comes from `GET /api/species/{species_id}` — cached in-memory
 * so re-opening the sheet is instant.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Leaf, Sprout, Shield, MapPin, Sparkles } from 'lucide-react';
import api from '../../lib/api';

// Simple module-level cache — key is species_id
const _cache = new Map();

async function loadSpecies(speciesId) {
  if (!speciesId) return null;
  // Synthetic emergency-AI species IDs (e.g. `emrg-reptiles`) don't have
  // a species doc — skip the fetch so we don't spam the console with
  // 404s during Practice-vs-emergency-AI battles.
  if (String(speciesId).startsWith('emrg-')) return null;
  if (_cache.has(speciesId)) return _cache.get(speciesId);
  try {
    const { data } = await api.get(`/species/${speciesId}`);
    _cache.set(speciesId, data);
    return data;
  } catch {
    return null;
  }
}

/**
 * Static arena-level ecology role — surfaces when we don't have species
 * habitat text. Same tone as EcologyIntroCard.
 */
const ARENA_ROLE = {
  forest:    'Forests connect canopy, understory and soil communities.',
  wetland:   'Wetlands filter water, buffer floods and feed migratory birds.',
  swamp:     'Swamps are engineered by beavers and store enormous biomass.',
  mountain:  'Mountains host altitudinal life-zones stacked over short distances.',
  alpine:    'Alpine zones shelter cold-adapted specialists above the tree-line.',
  grassland: 'Grasslands are burned and grazed into a mosaic of habitats.',
  prairie:   'Prairies are burned and grazed into a mosaic of habitats.',
  tundra:    'Tundra soils hold the world\'s largest terrestrial carbon store.',
  ocean:     'Oceans drive climate and carry the largest food web on Earth.',
  desert:    'Deserts reward extreme physiological specialisation.',
};

export default function EcologySheet({
  open,
  onClose,
  chSnapshot,
  opSnapshot,
  arenaKey,
  arenaLabel,
  isSharedHabitat,
}) {
  const [chSpecies, setChSpecies] = useState(null);
  const [opSpecies, setOpSpecies] = useState(null);
  useEffect(() => {
    if (!open) return;
    let dead = false;
    loadSpecies(chSnapshot?.species_id).then((s) => { if (!dead) setChSpecies(s); });
    loadSpecies(opSnapshot?.species_id).then((s) => { if (!dead) setOpSpecies(s); });
    return () => { dead = true; };
  }, [open, chSnapshot?.species_id, opSnapshot?.species_id]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[90] bg-black/70 flex items-end justify-center"
          onClick={onClose}
          data-testid="ecology-sheet-backdrop"
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 24, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-lg h-[64svh] rounded-t-3xl bg-[#0a1a12] border-t border-x border-emerald-400/30 shadow-2xl overflow-hidden flex flex-col"
            data-testid="ecology-sheet"
            role="dialog"
            aria-label="Ecology details"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/5 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <Leaf className="w-4 h-4 text-emerald-300 shrink-0" strokeWidth={2.4} />
                <div className="min-w-0">
                  <p className="text-[9px] uppercase tracking-[0.3em] text-emerald-300">
                    {isSharedHabitat ? 'Shared habitat' : 'Ecological arena'}
                  </p>
                  <p className="font-serif text-base leading-tight truncate">
                    {arenaLabel || arenaKey || 'This arena'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="ecology-sheet-close"
                aria-label="Close ecology details"
                className="h-8 w-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Arena role line */}
            <p className="px-4 pt-3 text-xs text-emerald-100/80 shrink-0" data-testid="ecology-sheet-arena-role">
              {ARENA_ROLE[String(arenaKey || '').toLowerCase()] || 'Every arena is chosen from both species\' real habitat associations.'}
            </p>

            {/* Species blocks */}
            <div className="flex-1 overflow-y-auto px-4 pb-6 pt-3 space-y-4">
              <SpeciesEcologyBlock
                side="challenger"
                snapshot={chSnapshot}
                species={chSpecies}
              />
              <SpeciesEcologyBlock
                side="opponent"
                snapshot={opSnapshot}
                species={opSpecies}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SpeciesEcologyBlock({ side, snapshot, species }) {
  const name = snapshot?.name || 'Unknown species';
  const category = snapshot?.species_category;
  const rows = [
    species?.habitat && { icon: Sprout,   label: 'Habitat',       text: species.habitat },
    species?.diet    && { icon: Leaf,     label: 'Diet',          text: species.diet },
    species?.ecological_role && { icon: Sparkles, label: 'Ecological role', text: species.ecological_role },
    species?.range   && { icon: MapPin,   label: 'Range',         text: species.range },
    species?.conservation_status && {
      icon: Shield,
      label: 'Conservation status',
      text: species.conservation_status,
    },
  ].filter(Boolean);

  const facts = Array.isArray(species?.fun_facts) ? species.fun_facts.slice(0, 3) : [];

  return (
    <div
      className="rounded-2xl bg-black/30 border border-white/5 p-3"
      data-testid={`ecology-sheet-${side}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-1 h-8 rounded-full ${side === 'challenger' ? 'bg-emerald-400' : 'bg-rose-400'}`} />
        <div className="min-w-0">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground">
            {side === 'challenger' ? 'Your fighter' : 'Opponent'}
          </p>
          <p className="font-serif text-sm leading-tight truncate">{name}</p>
          {category && <p className="text-[10px] text-muted-foreground">{category}</p>}
        </div>
      </div>

      {rows.length === 0 && facts.length === 0 && (
        <p className="text-[11px] text-muted-foreground italic">
          No ecology data on file for this species yet.
        </p>
      )}

      {rows.length > 0 && (
        <div className="space-y-2">
          {rows.map(({ icon: Icon, label, text }) => (
            <div key={label} className="flex gap-2 text-[12px] leading-snug">
              <Icon className="w-3.5 h-3.5 text-emerald-300 shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <span className="text-emerald-200 font-medium">{label}: </span>
                <span className="text-white/85">{text}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {facts.length > 0 && (
        <ul className="mt-2 space-y-1 list-disc list-inside marker:text-emerald-400">
          {facts.map((f, i) => (
            <li key={i} className="text-[11px] text-white/75 leading-snug">{f}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
